'use client'

import { useState, useEffect } from 'react'
import { tzAbbreviation } from '@/lib/timezones'

/** Short timezone label (e.g. "AEST") for an instant, with a leading space,
 *  so sale times shown to visitors in other zones are unambiguous. Empty
 *  string if no tz. Uses the shared resolver so the abbreviation matches the
 *  rest of the storefront (and never falls back to a raw "GMT+10"). */
function zoneAbbr(date: Date, timeZone?: string): string {
  if (!timeZone) return ''
  const abbr = tzAbbreviation(date, timeZone)
  return abbr ? ` ${abbr}` : ''
}

interface EventData {
  id: string
  slug: string
  name: string
  eventType?: string
  timezone?: string
  description: string
  startDate: Date
  endDate?: Date
  imageUrl?: string
  presaleEnabled?: boolean
  presaleStartDate?: string | null
  presaleEndDate?: string | null
  publicSaleStartDate?: string | null
  saleEndDate?: string | null
  venue: {
    id: string
    name: string
    address: string | null
    city: string | null
    state: string | null
    capacity?: number | null
  }
  ticketTypes: Array<{
    id: string
    name: string
    description: string
    price: number
    quantity: number
    sold: number
    reserved: number
    isVisible: boolean
    sortOrder: number
    minQuantity: number
    maxQuantity: number
    quantityInterval: number
    isAvailable: boolean
    availableQuantity: number
    badgeText: string | null
    backgroundColor: string | null
    textColor: string | null
    isFeature: boolean
    [key: string]: unknown
  }>
  schedules?: Array<{
    id: string
    startDate: string
    endDate: string
    recurrenceRule: string | null
    timeSlots: Array<{
      id: string
      startTime: string
      endTime: string
      capacity: number
      price: number | null
      label: string | null
      dayOfWeek: number | null
    }>
    blackoutDates: Array<{
      date: string
      reason: string | null
    }>
  }>
}

type SalePhase =
  | { type: 'presale_upcoming'; date: Date }
  | { type: 'presale_active'; endDate: Date | null }
  | { type: 'public_upcoming'; date: Date }
  | { type: 'on_sale' }
  | { type: 'sale_ended' }
  | null

function getEventSalePhase(event: EventData): SalePhase {
  const now = new Date()
  if (event.saleEndDate && now > new Date(event.saleEndDate)) {
    return { type: 'sale_ended' }
  }
  if (event.presaleEnabled && event.presaleStartDate) {
    const presaleStart = new Date(event.presaleStartDate)
    if (now < presaleStart) {
      return { type: 'presale_upcoming', date: presaleStart }
    }
    // Presale has started
    if (event.presaleEndDate) {
      const presaleEnd = new Date(event.presaleEndDate)
      if (now < presaleEnd) {
        return { type: 'presale_active', endDate: presaleEnd }
      }
    }
  }
  if (event.publicSaleStartDate) {
    const publicStart = new Date(event.publicSaleStartDate)
    if (now < publicStart) {
      return { type: 'public_upcoming', date: publicStart }
    }
  }
  return null
}

function formatCountdownStr(target: Date): string {
  const diff = target.getTime() - Date.now()
  if (diff <= 0) return ''
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)
  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
  return `${minutes}m ${seconds}s`
}

function SaleCountdownCard({ event, eventStatus, compact }: { event: EventData; eventStatus?: string; compact?: boolean }) {
  // Initial phase=null so SSR + first client render agree (no React
  // #425). Real phase computed in the useEffect tick below.
  const [phase, setPhase] = useState<SalePhase>(null)
  const [countdown, setCountdown] = useState('')

  useEffect(() => {
    const update = () => {
      const p = getEventSalePhase(event)
      setPhase(p)
      if (p?.type === 'presale_upcoming') {
        setCountdown(formatCountdownStr(p.date))
      } else if (p?.type === 'presale_active' && p.endDate) {
        setCountdown(formatCountdownStr(p.endDate))
      } else if (p?.type === 'public_upcoming') {
        setCountdown(formatCountdownStr(p.date))
      } else {
        setCountdown('')
      }
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [event])

  // Terminal states (sold out / cancelled) — simple card
  if (eventStatus === 'SOLD_OUT') {
    return (
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card p-6 text-center">
        <p className="text-text-secondary text-sm">Tickets are no longer available for this event.</p>
      </div>
    )
  }
  if (eventStatus === 'CANCELLED') {
    return (
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card p-6 text-center">
        <p className="text-text-secondary text-sm">This event has been cancelled.</p>
      </div>
    )
  }

  // Fallback: if phase detection returns null but we're still being rendered,
  // show a message based on raw date fields
  if (!phase) {
    if (event.publicSaleStartDate && new Date(event.publicSaleStartDate) > new Date()) {
      const tz = event.timezone || undefined
      const saleStart = new Date(event.publicSaleStartDate)
      const dateStr = saleStart.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz })
      const timeStr = saleStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }) + zoneAbbr(saleStart, tz)
      if (compact) {
        return (
          <p className="text-text-primary font-semibold text-sm">
            On sale {dateStr} at {timeStr}
          </p>
        )
      }
      return (
        <div className="bg-surface rounded-2xl border border-surface-border shadow-card p-6 text-center">
          <h3 className="text-lg font-semibold text-text-primary mb-2">On Sale Soon</h3>
          <p className="text-text-secondary text-sm">{dateStr} at {timeStr}</p>
        </div>
      )
    }
    if (event.saleEndDate && new Date(event.saleEndDate) < new Date()) {
      return (
        <div className="bg-surface rounded-2xl border border-surface-border shadow-card p-6 text-center">
          <p className="text-text-secondary text-sm">Ticket sales have ended for this event.</p>
        </div>
      )
    }
    return null
  }

  // Parse countdown into segments for the digit display
  const segments = countdown ? countdown.split(' ') : []

  if (phase.type === 'presale_upcoming' || phase.type === 'public_upcoming') {
    const targetDate = phase.type === 'presale_upcoming' ? phase.date : (phase as { type: 'public_upcoming'; date: Date }).date
    const label = phase.type === 'presale_upcoming' ? 'Presale Starts' : 'On Sale'
    const dateSource = phase.type === 'presale_upcoming' ? event.presaleStartDate! : event.publicSaleStartDate!

    if (compact) {
      return (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-text-primary font-semibold text-sm">{label}</p>
            <p className="text-text-tertiary text-xs">
              {new Date(dateSource).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: event.timezone || undefined })}
              {' at '}
              {new Date(dateSource).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: event.timezone || undefined })}
              {zoneAbbr(new Date(dateSource), event.timezone)}
            </p>
          </div>
          {countdown && (
            <p className="font-mono font-bold text-primary text-sm">{countdown}</p>
          )}
        </div>
      )
    }

    return (
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
        <div className="p-5 border-b border-surface-border">
          <h3 className="text-lg font-semibold text-text-primary">{label}</h3>
        </div>
        <div className="p-5">
          {/* Countdown digits */}
          {segments.length > 0 && (
            <div className="flex items-center justify-center gap-2 mb-4">
              {segments.map((seg, i) => {
                const num = seg.replace(/\D/g, '')
                const unit = seg.replace(/\d/g, '')
                return (
                  <div key={i} className="flex items-baseline gap-0.5">
                    <span className="bg-surface-secondary rounded-lg px-3 py-2 font-mono text-2xl font-bold text-text-primary tabular-nums">
                      {num}
                    </span>
                    <span className="text-text-tertiary text-xs font-medium uppercase">{unit}</span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Date & time */}
          <div className="text-center">
            <p className="text-text-secondary text-sm">
              {new Date(dateSource).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: event.timezone || undefined })}
              {' at '}
              {new Date(dateSource).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: event.timezone || undefined })}
              {zoneAbbr(new Date(dateSource), event.timezone)}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (phase.type === 'presale_active') {
    if (compact) {
      return (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
            </span>
            <p className="text-text-primary font-semibold text-sm">Presale Live</p>
          </div>
          {countdown && (
            <p className="text-text-tertiary text-xs">Ends in {countdown}</p>
          )}
        </div>
      )
    }

    return (
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden">
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success" />
              </span>
              <p className="text-text-primary font-semibold">Presale is live</p>
            </div>
            {countdown && (
              <p className="text-text-tertiary text-sm">Ends in <span className="font-mono font-medium text-text-secondary">{countdown}</span></p>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (phase.type === 'sale_ended') {
    return (
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card p-6 text-center">
        <p className="text-text-secondary text-sm">Ticket sales have ended for this event.</p>
      </div>
    )
  }

  return null
}

export default SaleCountdownCard
